import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ProjectService } from '../../services/ProjectService/project';
import { TraceService } from '../../services/TraceService/trace';
import { ExportRequestService } from '../../services/ExportRequestService/export-request';
import { AuthService } from '../../services/AuthService/auth';
import { ParticipantResponse } from '../../models/project.dto';
import { TraceEventResponse } from '../../models/trace.dto';
import { extractErrorMessage } from '../../shared/error-utils';
import { ErrorBanner } from '../../shared/components/error-banner/error-banner';
import { EmptyState } from '../../shared/components/empty-state/empty-state';
import { CompanySelect } from '../../shared/components/company-select/company-select';
import { GlobeLoader } from '../../shared/components/globe-loader/globe-loader';
import { TraceLoader } from '../../shared/components/trace-loader/trace-loader';

@Component({
  selector: 'app-raise-export-request',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    RouterLink,
    ErrorBanner,
    EmptyState,
    CompanySelect,
    GlobeLoader,
    TraceLoader,
  ],
  templateUrl: './raise-export-request.html',
  styleUrl: './raise-export-request.scss',
})
export class RaiseExportRequest implements OnInit {
  /**
   * Shortest time the trace loader stays on screen. One hop of its animation is 800ms, and a trace
   * against a warm backend can return in under 100ms — releasing the loader that fast reads as a
   * flicker rather than as feedback, so a fast trace is held to one complete hop.
   */
  private static readonly MIN_TRACE_LOADER_MS = 900;

  projectId = '';

  /** Every active participant on the project, as loaded — unfiltered. */
  private allParticipants = signal<ParticipantResponse[]>([]);
  importerCandidates = signal<ParticipantResponse[]>([]);
  importerCompanyId = '';
  epc = '';

  /**
   * A SUPERADMIN has no company of its own and must pick which participant is raising this
   * request — a company-scoped caller never sees this control and always raises as its own
   * company (see `ownCompanyId`/`resolvedExporterCompanyId`).
   */
  isPlatformCaller = false;
  exporterCandidates = signal<ParticipantResponse[]>([]);
  exporterCompanyId = '';

  isLoadingParticipants = signal(false);
  participantsError = signal('');

  isTracing = signal(false);
  traceError = signal('');
  traceUnavailable = signal(false);
  events = signal<TraceEventResponse[]>([]);
  selectedEventIds = signal<Set<string>>(new Set());
  private traceStartedAt = 0;

  isSubmitting = signal(false);
  submitError = signal('');

  // Describes the sequence the backend actually runs inside create(): it finalises the event
  // selection, then writes the exporter's entry to the chain. Advanced on a timer, not by the
  // server — see GlobeLoader.
  readonly submitSteps = [
    'Finalising the selected events…',
    'Notarising the payload…',
    'Writing to the chain…',
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private traceService: TraceService,
    private exportRequestService: ExportRequestService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.projectId = this.route.snapshot.paramMap.get('id') ?? '';
    this.isPlatformCaller = !this.auth.getSession()?.companyId;
    this.loadParticipants();
  }

  private loadParticipants(): void {
    this.isLoadingParticipants.set(true);
    this.participantsError.set('');
    const ownCompanyId = this.auth.getSession()?.companyId;

    this.projectService.get(this.projectId).subscribe({
      next: (res) => {
        this.isLoadingParticipants.set(false);
        this.allParticipants.set(res.data.participants);
        if (this.isPlatformCaller) {
          // No own company to exclude — the admin picks both sides, so every participant is a
          // candidate sender, and the importer list is narrowed once a sender is chosen.
          this.exporterCandidates.set(res.data.participants);
          this.importerCandidates.set(res.data.participants);
        } else {
          this.importerCandidates.set(res.data.participants.filter((p) => p.companyId !== ownCompanyId));
        }
      },
      error: (err: HttpErrorResponse) => {
        this.isLoadingParticipants.set(false);
        this.participantsError.set(extractErrorMessage(err, 'Failed to load project participants.'));
      },
    });
  }

  /**
   * Picking a sender (platform caller only) narrows the importer list to everyone else, and
   * invalidates any trace already run — a trace is scoped to the sender's own country's
   * middleware, so it no longer describes the newly chosen sender.
   */
  onExporterChange(): void {
    this.exporterCandidates.set(this.allParticipants());
    this.importerCandidates.set(
      this.allParticipants().filter((p) => p.companyId !== this.exporterCompanyId)
    );
    if (this.importerCompanyId === this.exporterCompanyId) {
      this.importerCompanyId = '';
    }
    this.events.set([]);
    this.selectedEventIds.set(new Set());
  }

  /** The company this request is raised as — the caller's own, or the admin's chosen sender. */
  private get resolvedExporterCompanyId(): string {
    return this.auth.getSession()?.companyId ?? this.exporterCompanyId;
  }

  get canTrace(): boolean {
    return (
      !!this.resolvedExporterCompanyId &&
      !!this.importerCompanyId &&
      !!this.epc.trim() &&
      !this.isTracing()
    );
  }

  trace(): void {
    if (!this.canTrace) {
      return;
    }

    this.isTracing.set(true);
    this.traceError.set('');
    this.traceUnavailable.set(false);
    this.events.set([]);
    this.selectedEventIds.set(new Set());
    this.traceStartedAt = Date.now();

    this.traceService.trace(this.epc, false, this.resolvedExporterCompanyId).subscribe({
      next: (res) => {
        this.settleTrace(() => this.events.set(res.data.events));
      },
      error: (err: HttpErrorResponse) => {
        this.settleTrace(() => {
          if (err.status === 503) {
            this.traceUnavailable.set(true);
            return;
          }
          this.traceError.set(extractErrorMessage(err, 'Failed to trace EPC.'));
        });
      },
    });
  }

  /**
   * Drops the trace loader and applies the outcome, waiting out the remainder of
   * {@link MIN_TRACE_LOADER_MS} first so the loader is never on screen for less than one hop.
   */
  private settleTrace(apply: () => void): void {
    const remaining = RaiseExportRequest.MIN_TRACE_LOADER_MS - (Date.now() - this.traceStartedAt);

    if (remaining <= 0) {
      this.isTracing.set(false);
      apply();
      return;
    }

    setTimeout(() => {
      this.isTracing.set(false);
      apply();
    }, remaining);
  }

  isSelected(eventId: string): boolean {
    return this.selectedEventIds().has(eventId);
  }

  toggleEvent(eventId: string): void {
    const next = new Set(this.selectedEventIds());
    if (next.has(eventId)) {
      next.delete(eventId);
    } else {
      next.add(eventId);
    }
    this.selectedEventIds.set(next);
  }

  get allSelected(): boolean {
    return this.events().length > 0 && this.selectedEventIds().size === this.events().length;
  }

  get someSelected(): boolean {
    return this.selectedEventIds().size > 0 && !this.allSelected;
  }

  toggleSelectAll(): void {
    this.selectedEventIds.set(this.allSelected ? new Set() : new Set(this.events().map((e) => e.eventId)));
  }

  get canSubmit(): boolean {
    return (
      !!this.resolvedExporterCompanyId &&
      !!this.importerCompanyId &&
      this.selectedEventIds().size > 0 &&
      !this.isSubmitting()
    );
  }

  submit(): void {
    if (!this.canSubmit) {
      return;
    }

    this.isSubmitting.set(true);
    this.submitError.set('');

    this.exportRequestService
      .create(this.projectId, {
        exporterCompanyId: this.isPlatformCaller ? this.exporterCompanyId : undefined,
        importerCompanyId: this.importerCompanyId,
        epc: this.epc,
        eventIds: Array.from(this.selectedEventIds()),
      })
      .subscribe({
        next: (res) => {
          this.isSubmitting.set(false);
          this.router.navigate(['/export-requests', res.data.id]);
        },
        error: (err: HttpErrorResponse) => {
          this.isSubmitting.set(false);
          this.submitError.set(
            extractErrorMessage(err, 'Failed to submit export request.') +
              ' If a chain write failed, the request may still have been recorded — check your outgoing requests.'
          );
        },
      });
  }
}
